import { useState } from 'react';

import { CreditCalculationModel } from '../../../../../../../backend/src/billing/billing-types';
import { resolveBillingPlan } from '../../../../../../../backend/src/billing/resolve-billing-plan';
import type { BillingPlanHistoryResponseDto } from '../../../../../../../backend/src/organisation/dto/billing-plan-history.response.dto';
import { PricingModel } from '../../../../../../../backend/src/types/types';
import { useDeleteFutureBillingPlan } from '@/api/mutations/useDeleteFutureBillingPlan';
import { useGetOrganisationBillingPlanHistory } from '@/api/queries/useGetOrganisationBillingPlanHistory';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { SettingsListHeader, SettingsListItem, SettingsSection } from '@/new/components/settings-section';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import { formatDatetimeForTable } from '@/new/lib/table-utils';

function formatDate(value: Date | null) {
  return value
    ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'Not set';
}

function planSummary(plan: BillingPlanHistoryResponseDto) {
  if (plan.pricingModel === PricingModel.SeatBased) {
    const seats = `${plan.seatCount ?? 'No'} seat${plan.seatCount === 1 ? '' : 's'}`;
    const limit = plan.defaultCreditLimit?.toLocaleString('en-GB') ?? 'No';
    return `Seat-based · ${seats} · ${limit} default credits per user`;
  }

  return `Usage-based · ${plan.maxCredits?.toLocaleString('en-GB') ?? 'No'} maximum credits`;
}

function effectiveRange(plan: BillingPlanHistoryResponseDto) {
  const end = plan.effectiveUntil ? formatDatetimeForTable(plan.effectiveUntil) : 'No end date';
  return `${formatDatetimeForTable(plan.effectiveFrom)} – ${end}`;
}

/** Mirrors the server rule: only plans whose effective range and billing period both start later can be deleted. */
function hasNotStarted(plan: BillingPlanHistoryResponseDto, now: Date) {
  return new Date(plan.effectiveFrom) > now && new Date(plan.billingStartDate ?? plan.effectiveFrom) > now;
}

export function BillingPlanHistory({ organisationId }: { organisationId: string }) {
  const { data: plans = [], isLoading, isError } = useGetOrganisationBillingPlanHistory(organisationId);
  const { mutate: deletePlan, isLoading: isDeleting } = useDeleteFutureBillingPlan(organisationId);
  const [planToDelete, setPlanToDelete] = useState<BillingPlanHistoryResponseDto | null>(null);

  const now = new Date();
  const resolved = resolveBillingPlan(plans, now);

  return (
    <SettingsSection
      title="Billing plan history"
      description="Every billing plan recorded for this organisation, newest first."
    >
      <SettingsListHeader
        label={
          isLoading
            ? 'Loading billing plans…'
            : isError
              ? 'Billing plans could not be loaded'
              : `${plans.length} billing plan${plans.length === 1 ? '' : 's'}`
        }
      />

      {!isLoading && !isError && plans.length === 0 && (
        <div className="px-5 py-6 text-center text-sm text-gray-500">No billing plans have been recorded.</div>
      )}

      {plans.length > 0 && (
        <div className="max-h-96 divide-y divide-gray-200 overflow-y-auto">
          {plans.map((plan, index) => (
            <SettingsListItem
              key={plan.id}
              leading={<span className="text-sm text-gray-400 tabular-nums">{index + 1}.</span>}
              title={`Billing period · ${formatDate(plan.billingStartDate)} – ${formatDate(plan.billingEndDate)}`}
              description={
                <span>
                  {planSummary(plan)} ·{' '}
                  {plan.creditCalculationModel === CreditCalculationModel.CostBased ? 'Cost-based' : 'Legacy'} ·{' '}
                  {plan.pagesPerCredit} {plan.pagesPerCredit === 1 ? 'page' : 'pages'} per credit ·{' '}
                  {plan.disableBillingForKimThreads ? 'Kim threads not billed · ' : ''}Effective {effectiveRange(plan)}
                </span>
              }
              actions={
                resolved.source === 'current' && resolved.plan?.id === plan.id ? (
                  <Badge>
                    <span className="mr-1 size-1.25 rounded-full bg-green-500" />
                    Current Plan
                  </Badge>
                ) : hasNotStarted(plan, now) ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    onClick={() => setPlanToDelete(plan)}
                    aria-label="Delete scheduled billing plan"
                    title="Delete scheduled billing plan"
                    className="text-gray-400 hover:text-red-600"
                  >
                    <Icon className="lucide--trash-2 size-4" />
                  </Button>
                ) : undefined
              }
            />
          ))}
        </div>
      )}

      <ConfirmationModal
        isOpen={planToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPlanToDelete(null);
        }}
        title="Delete scheduled billing plan?"
        confirmLabel="Delete plan"
        confirmVariant="destructive"
        cancelVariant="ghost"
        isLoading={isDeleting}
        onConfirm={() => {
          if (!planToDelete) return;
          deletePlan(planToDelete.id, { onSuccess: () => setPlanToDelete(null) });
        }}
      >
        {planToDelete && (
          <p className="text-sm text-gray-600">
            The billing period {formatDate(planToDelete.billingStartDate)} – {formatDate(planToDelete.billingEndDate)}{' '}
            has not started. Deleting it removes the plan; current settings and usage are unchanged.
          </p>
        )}
      </ConfirmationModal>
    </SettingsSection>
  );
}
